<template>
    <search-layout
        v-if="loaded"
        :map-expanded="mapExpanded"
        :offers-count="filteredOffers.length"
        :search-active="searchActive"
    >
        <template v-slot:breadcrumbs>
            <flight-breadcrumbs :search-request="searchRequest" />
        </template>
        <template v-slot:left-sidebar>
            <flight-search-summary :search-request="searchRequest" :search-active="searchActive" />
            <sort-select v-model="sortKey" :items="sortItems" />
            <flight-filters
                :filter-values="filterValues"
                :filters="searchResponse.filters"
                :filtered-offers-count="filteredOffers.length"
                :currency="currency"
                @change="changeFilter"
                @reset="resetFilters"
            />
        </template>
        <template v-slot:main-content>
            <search-progress
                :filtered-offers-count="filteredOffers.length"
                :total-offers-count="searchResponse.offers.length"
                :search-active="searchActive"
                :hide-stop-btn="$route.name === 'flightsAvailability'"
                @stop="stopSearch"
            />
            <v-dialog v-if="$route.name === 'flights'" v-model="flexibleFlightsModal" width="100%" max-width="1200px">
                <template v-slot:activator="{props}">
                    <v-btn variant="flat" block v-bind="props" @click="getFlexibleFlights()">
                        {{ $t('flights.flexible') }}
                    </v-btn>
                </template>
                <v-card>
                    <v-card-text>
                        <flexible-flights :flights="flexibleFlights" :dates="flexibleDates" />
                    </v-card-text>
                </v-card>
            </v-dialog>
            <flight-best-offers :offers="filteredOffers" class="my-3" @sort="sortKey = $event" />
            <flight-offers :offers="offers" />
            <pagination v-model="page" :pages="pages" />
            <conditions-modal>
                <template v-slot:info>
                    <flight-conditions-modal-info />
                </template>
            </conditions-modal>
        </template>
        <template v-slot:right-sidebar>
            <client-only>
                <flight-map v-model="mapExpanded" :points="mapPoints" />
            </client-only>
        </template>
        <overlay-progress :active="filterActive && !searchActive" />
    </search-layout>
</template>

<script lang="ts">
    import {Component, mixins, Setup} from 'vue-facing-decorator'
    import Pagination from '@/components/search/Pagination.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import FlightBreadcrumbs from '@/components/flights/FlightBreadcrumbs.vue'
    import FlightSearchSummary from '@/components/flights/search/FlightSearchSummary.vue'
    import SearchProgress from '~src/components/search/searchProgress.src'
    import FlightMap from '@/components/flights/search/FlightMap.vue'
    import FlightFilters from '~src/components/flights/search/flightFilters.src'
    import FlightOffers from '@/components/flights/search/offers/FlightOffers.vue'
    import FlightBestOffers from '@/components/parts/Flights/FlightBestOffers.vue'
    import {SearchPageBase} from '@/mixins/SearchPageBase.vue'
    import {FlightStoreMixin} from '@/mixins/FlightStoreMixin.vue'
    import SearchLayout from '@/components/parts/SearchLayout.vue'
    import {FlightPageMixin} from '@/components/flights/mixins/FlightPageMixin.vue'
    import SortSelect from '@/components/search/SortSelect.vue'
    import FlightConditionsModalInfo from '@/components/flights/snippets/FlightConditionsModalInfo.vue'
    import _Tmp_SearchPageBase, {Tmp_SearchPageBase} from '@/mixins/Tmp_SearchPageBase.vue'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import OverlayProgress from '@/components/OverlayProgress.vue'
    import FlexibleFlights from '@/components/flights/FlexibleFlights.vue'
    import {useFlightRuntimeData} from '~/composables/flight'
    import {callComponentComputed} from '~/utils/vue-class-helpers'

    @Component({
        components: {
            OverlayProgress,
            FlightConditionsModalInfo,
            SortSelect,
            FlightSearchSummary,
            SearchProgress,
            FlightMap,
            FlightFilters,
            FlightOffers,
            Pagination,
            FlightBreadcrumbs,
            ConditionsModal,
            SearchLayout,
            FlightBestOffers,
            FlexibleFlights,
        },
        async setup() {
            const route = useRoute()
            await useAsyncData(`flight-${route.name.toString()}`, async () => {
                flightsRuntimeStore.SET_SEARCH_TYPE(route.name === 'flights' ? 'price' : 'schedule')
                await useFlightRuntimeData(route)
                return true
            })
        },
    })
    export class FlightsPage extends mixins(
        FlightPageMixin,
        FlightStoreMixin,
        SearchPageMixin,
        SearchPageBase,
        Tmp_SearchPageBase
    ) {
        @Setup(() => useOfferExpired()) showOfferExpired: Function

        flexibleFlightsModal = false

        override async mounted() {
            EventBus.$on('OFFER_EXPIRED_EVENT', () => this.showOfferExpired(this.search))
        }

        unmounted() {
            EventBus.$off('OFFER_EXPIRED_EVENT')
        }

        getFlexibleFlights() {
            const rq = {...this.$route.query}
            rq.flexibleDays = 3
            rq.convertToCurrency = persistentStore.currency
            this.productRuntimeStore.searchFlexibleFlights(rq)
        }

        async search() {
            this.flexibleFlightsModal = false
            await this.productRuntimeStore.search(this.searchRequest)
        }

        get mapPoints() {
            let result = []
            flightsRuntimeStore.routes.forEach(({arrival, departure}) => {
                result = [...result, departure, arrival]
            })
            return result
        }

        get flexibleFlights() {
            return this.productRuntimeStore.flexibleFlights
        }

        get flexibleDates() {
            return this.productRuntimeStore.flexibleDates
        }

        get sortItems() {
            return [
                ...callComponentComputed(this, _Tmp_SearchPageBase, 'sortItems'),
                {
                    text: this.$t('sort.best'),
                    value: 'best',
                },
                {
                    text: this.$t('sort.duration_asc'),
                    value: 'durationAsc',
                },
                {
                    text: this.$t('sort.duration_desc'),
                    value: 'durationDesc',
                },
                {
                    text: this.$t('sort.airline'),
                    value: 'airline',
                },
                {
                    text: this.$t('sort.departure_time'),
                    value: 'departureTime',
                },
                {
                    text: this.$t('sort.arrival_time'),
                    value: 'arrivalTime',
                },
            ]
        }
    }

    export default FlightsPage
</script>
